/*
 * @Author: Ryan
 * @Date: 2026-01-19
 * @Description: Real-world use cases section with accordion-style tabs and video player
 */
import React, { useRef, useState } from 'react';

interface UseCase {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
  posterUrl: string;
}

const useCases: UseCase[] = [
  {
    id: 'ecommerce',
    title: 'E-commerce',
    description:
      'Create high-converting product videos at scale, from detail pages to paid ads and marketplaces. AI hosts present features, benefits, and use cases clearly, helping shoppers understand products faster, build trust, and make purchase decisions with confidence—without the cost of repeated photoshoots or reshoots.',
    videoUrl:
      'https://assets.falcocut.ai/ai-product-avatar/where-ai-product-avatars-deliver-the-biggest-impact-ecommerce.mp4',
    posterUrl:
      'https://assets.falcocut.ai/ai-product-avatar/where-ai-product-avatars-deliver-the-biggest-impact-ecommerce-poster.webp',
  },
  {
    id: 'marketing',
    title: 'Marketing & Advertising',
    description:
      'Produce campaign-ready videos in multiple formats so you can test hooks, refine messaging, and scale winning creatives faster. Human-like hosts add personality to your ads, making them more engaging across social feeds and landing pages.',
    videoUrl:
      'https://assets.falcocut.ai/ai-product-avatar/where-ai-product-avatars-deliver-the-biggest-impact-marketing.mp4',
    posterUrl:
      'https://assets.falcocut.ai/ai-product-avatar/where-ai-product-avatars-deliver-the-biggest-impact-marketing-poster.webp',
  },
  {
    id: 'customer-support',
    title: 'Customer Support',
    description:
      'Turn complex instructions into simple, guided explainers that reduce confusion and cut down on repetitive tickets. On-screen product guides walk users through each step, improving satisfaction and lowering return rates.',
    videoUrl:
      'https://assets.falcocut.ai/ai-product-avatar/where-ai-product-avatars-deliver-the-biggest-impact-customer-support.mp4',
    posterUrl:
      'https://assets.falcocut.ai/ai-product-avatar/where-ai-product-avatars-deliver-the-biggest-impact-customer-support-poster.webp',
  },
  {
    id: 'influencers',
    title: 'Influencers & Creators',
    description:
      "Generate try-ons, product showcases, and storytelling clips even when you can't film in person. Virtual stand-ins help you maintain a consistent posting rhythm and keep your audience engaged across platforms.",
    videoUrl:
      'https://assets.falcocut.ai/ai-product-avatar/where-ai-product-avatars-deliver-the-biggest-impact-influencers.mp4',
    posterUrl:
      'https://assets.falcocut.ai/ai-product-avatar/where-ai-product-avatars-deliver-the-biggest-impact-influencers-poster.webp',
  },
  {
    id: 'sales',
    title: 'Sales & Business Development',
    description:
      'Deliver personalized product walk-throughs and pitch videos that feel tailored to each prospect. Digital spokespeople communicate value clearly and consistently, helping you spark interest and move deals forward faster.',
    videoUrl:
      'https://assets.falcocut.ai/ai-product-avatar/where-ai-product-avatars-deliver-the-biggest-impact-sales.mp4',
    posterUrl:
      'https://assets.falcocut.ai/ai-product-avatar/where-ai-product-avatars-deliver-the-biggest-impact-sales-poster.webp',
  },
];

const PlayButton: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
  >
    <rect x="1" y="1" width="62" height="62" rx="31" fill="white" fillOpacity="0.1" />
    <rect x="1" y="1" width="62" height="62" rx="31" stroke="white" strokeWidth="2" />
    <path
      d="M41.5038 30.3359C42.6913 31.1275 42.6913 32.8725 41.5038 33.6641L29.1094 41.9271C27.7803 42.8131 26 41.8603 26 40.2629L26 23.7371C26 22.1397 27.7803 21.1869 29.1094 22.0729L41.5038 30.3359Z"
      fill="white"
    />
  </svg>
);

interface UseCaseTabProps {
  useCase: UseCase;
  isActive: boolean;
  onClick: () => void;
}

const UseCaseTab: React.FC<UseCaseTabProps> = ({ useCase, isActive, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-2xl transition-colors duration-300 p-4 pad:p-5 ${
        isActive
          ? 'bg-[#7C3AED] text-white'
          : 'bg-[#F5F5F7] text-gray-700 hover:bg-[#EBEBED]'
      }`}
    >
      <h3
        className={`font-semibold text-base pad:text-base ${isActive ? 'text-white' : 'text-gray-800'}`}
      >
        {useCase.title}
      </h3>
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isActive ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="text-sm pad:text-base text-white/90 leading-relaxed">
            {useCase.description}
          </p>
        </div>
      </div>
    </button>
  );
};

const UseCasesSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const activeUseCase = useCases[activeIndex];

  const handleTabClick = (index: number) => {
    setActiveIndex(index);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handlePlayClick = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const handleVideoEnded = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };

  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Where AI Product Avatars Deliver the Biggest Impact
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          AI Product Avatars work across all kinds of industries, helping brands create
          fast, affordable, and engaging product videos. Whether you sell beauty items,
          tech gadgets, or home essentials, these studio‑quality AI avatars make it easy
          to show how your product works in real life—and why people should care.
        </p>

        {/* Content */}
        <div className="flex flex-col pad:flex-row gap-6 pad:gap-8 sm:gap-12 items-stretch">
          {/* Left - Video */}
          <div className="w-full pad:w-1/2 flex items-center justify-center">
            <div className="relative w-full rounded-2xl overflow-hidden bg-black">
              <video
                ref={videoRef}
                key={activeUseCase.videoUrl}
                src={activeUseCase.videoUrl}
                poster={activeUseCase.posterUrl}
                className="w-full h-auto cursor-pointer"
                onEnded={handleVideoEnded}
                onClick={handleVideoClick}
                playsInline
              />
              {/* Play Button Overlay */}
              {!isPlaying && (
                <button
                  type="button"
                  onClick={handlePlayClick}
                  className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors cursor-pointer"
                  aria-label="Play video"
                >
                  <PlayButton />
                </button>
              )}
            </div>
          </div>

          {/* Right - Tabs */}
          <div className="w-full pad:w-1/2 flex flex-col gap-3 pad:gap-4">
            {useCases.map((useCase, index) => (
              <UseCaseTab
                key={useCase.id}
                useCase={useCase}
                isActive={index === activeIndex}
                onClick={() => handleTabClick(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
